import { useEffect, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import { createUseStyles } from "react-jss";
import { useAtom } from "jotai";
import {
  CardFieldData,
  getVisibleCharText,
  getVisibleDetailLines
} from "../helpers/cardFields";
import { hapticTap } from "../helpers/haptics";
import { answeredState } from "../helpers/recoil";
import { Details, HskEntry } from "../helpers/types";
import { useLongPress } from "../helpers/useLongPress";
import CardDetailPopover from "./CardDetailPopover";

const FACE_COLORS = {
  front: "#2c3139",
  back: "#4a627a"
} as const;

const useStyles = createUseStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
    minWidth: 0
  },
  rowOuter: {
    display: "flex",
    alignItems: "stretch",
    gap: 8,
    width: "100%",
    minWidth: 0,
    touchAction: "manipulation"
  },
  rowOuterActionsHidden: {
    gap: 0
  },
  cardShell: {
    flex: 1,
    minWidth: 0,
    minHeight: 64,
    borderRadius: 10,
    border: "1px solid #404754",
    overflow: "hidden",
    transition: "border-color 0.35s ease, box-shadow 0.35s ease"
  },
  cardShellFlipped: {
    borderColor: "#7a94b8",
    boxShadow: "0 0 0 1px rgba(122, 148, 184, 0.35)"
  },
  cardShellCorrect: {
    borderColor: "#0F6E56",
    boxShadow: "0 0 0 1px rgba(15, 110, 86, 0.45)"
  },
  cardShellWrong: {
    borderColor: "#993C1D",
    boxShadow: "0 0 0 1px rgba(153, 60, 29, 0.45)"
  },
  flipZone: {
    width: "100%",
    minHeight: 64,
    cursor: "pointer"
  },
  flipScene: {
    perspective: 900,
    width: "100%"
  },
  flipCard: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    width: "100%",
    minHeight: 64,
    transformStyle: "preserve-3d",
    transition: "transform 0.45s cubic-bezier(0.4, 0.2, 0.2, 1)",
    transform: "rotateY(0deg)"
  },
  flipCardFlipped: {
    transform: "rotateY(180deg)"
  },
  face: {
    gridRow: "1 / 2",
    gridColumn: "1 / 2",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 64,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    boxSizing: "border-box"
  },
  faceFront: {
    backgroundColor: FACE_COLORS.front
  },
  faceBack: {
    backgroundColor: FACE_COLORS.back,
    transform: "rotateY(180deg)"
  },
  accent: {
    width: 3,
    flexShrink: 0,
    transition: "background-color 0.35s ease"
  },
  accentFront: {
    backgroundColor: FACE_COLORS.front
  },
  accentBack: {
    backgroundColor: FACE_COLORS.back
  },
  accentCorrect: {
    backgroundColor: "#0F6E56"
  },
  accentWrong: {
    backgroundColor: "#993C1D"
  },
  faceBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 10px 10px 8px",
    position: "relative"
  },
  char: {
    flexShrink: 0,
    fontSize: 24,
    fontWeight: 500,
    lineHeight: 1.1,
    color: "#e2e8f0",
    maxWidth: "38%"
  },
  details: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2
  },
  pinyin: {
    fontSize: 14,
    fontWeight: 500,
    color: "#5DCAA5",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  meaning: {
    fontSize: 12,
    color: "#cbd5e1",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical"
  },
  hint: {
    fontSize: 12,
    color: "#64748b",
    fontStyle: "italic"
  },
  sideTag: {
    position: "absolute",
    top: 6,
    right: 8,
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    pointerEvents: "none"
  },
  sideTagBack: {
    color: "#94a3b8"
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
    alignSelf: "center",
    padding: "2px 0"
  },
  actionBtn: {
    padding: "6px !important"
  },
  wrongBtn: {
    color: "#993C1D !important",
    border: "1px solid #993C1D !important",
    backgroundColor: "transparent !important",
    "&.active": {
      backgroundColor: "#FAECE7 !important",
      color: "#993C1D !important"
    }
  },
  correctBtn: {
    color: "#0F6E56 !important",
    border: "1px solid #0F6E56 !important",
    backgroundColor: "transparent !important",
    "&.active": {
      backgroundColor: "#E1F5EE !important",
      color: "#0F6E56 !important"
    }
  }
});

const RowFaceContent = ({
  cardDetails,
  detailsOptions,
  side,
  classes
}: {
  cardDetails: CardFieldData;
  detailsOptions: Details;
  side: "front" | "back";
  classes: ReturnType<typeof useStyles>;
}) => {
  const charText = getVisibleCharText(cardDetails, side, detailsOptions);
  const detailLines = getVisibleDetailLines(cardDetails, side, detailsOptions);

  return (
    <>
      {charText ? <span className={classes.char}>{charText}</span> : null}
      <div className={classes.details}>
        {detailLines.length > 0 ? (
          detailLines.map((line) =>
            line.kind === "pinyin" ? (
              <span key={line.key} className={classes.pinyin}>
                {line.value}
              </span>
            ) : (
              <span key={line.key} className={classes.meaning}>
                {line.value}
              </span>
            )
          )
        ) : (
          <span className={classes.hint}>
            {side === "front" ? "Tap to flip · Hold for details" : "Back side"}
          </span>
        )}
      </div>
    </>
  );
};

const DeckListRow = ({
  card,
  detailsOptions,
  isFlippedCount,
  isRevealedCount,
  showActions,
  onFlippedChange
}: {
  card: HskEntry;
  detailsOptions: Details;
  isFlippedCount: number;
  isRevealedCount: number;
  showActions: boolean;
  onFlippedChange?: (order: number, flipped: boolean) => void;
}) => {
  const classes = useStyles();
  const [isFlipped, setIsFlipped] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const skipFlipCount = useRef(true);
  const skipRevealCount = useRef(true);
  const [answeredList, setAnsweredList] = useAtom(answeredState);
  const [answer, setAnswer] = useState(answeredList.find((a) => a.order === card.order)?.answer);
  const cardDetails: CardFieldData = {
    charSimp: card.charSimp,
    charTrad: card.charTrad,
    pinyin: card.pinyin,
    meaning: card.meaning
  };

  useEffect(() => {
    const answeredValue = answeredList.find((a) => a.order === card.order)?.answer;
    if (answer === null) {
      return setAnsweredList(answeredList.filter((item) => item?.order !== card.order));
    }
    if (answer !== answeredValue) {
      return setAnsweredList(
        answeredValue === undefined
          ? [...answeredList, { order: card.order, answer }]
          : [...answeredList.filter((x) => x.order !== card.order), { order: card.order, answer }]
      );
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(answeredList.find((a) => a.order === card.order)?.answer);
  }, [answeredList, card.order]);

  useEffect(() => {
    if (skipFlipCount.current) {
      skipFlipCount.current = false;
      return;
    }
    setIsFlipped(true);
  }, [isFlippedCount]);

  useEffect(() => {
    if (skipRevealCount.current) {
      skipRevealCount.current = false;
      return;
    }
    setIsFlipped(false);
  }, [isRevealedCount]);

  useEffect(() => {
    onFlippedChange?.(card.order, isFlipped);
  }, [card.order, isFlipped, onFlippedChange]);

  const toggleFlip = () => {
    hapticTap();
    setIsFlipped((flipped) => !flipped);
  };

  const flipHandlers = useLongPress(() => setDetailOpen(true), toggleFlip);

  const markAnswer = (value: boolean) => {
    hapticTap();
    setAnswer(value);
  };

  const accentClass =
    answer === true ? classes.accentCorrect : answer === false ? classes.accentWrong : "";

  return (
    <>
      <div className={`${classes.rowOuter} ${!showActions ? classes.rowOuterActionsHidden : ""}`}>
        <div
          className={`${classes.cardShell} ${isFlipped ? classes.cardShellFlipped : ""} ${
            answer === true ? classes.cardShellCorrect : answer === false ? classes.cardShellWrong : ""
          }`}>
          <div
            className={classes.flipZone}
            role="button"
            tabIndex={0}
            aria-expanded={isFlipped}
            aria-label={`Card ${card.order + 1}. Tap to ${
              isFlipped ? "show front" : "show back"
            }. Hold for details.`}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleFlip();
              }
            }}
            {...flipHandlers}>
            <div className={classes.flipScene}>
              <div className={`${classes.flipCard} ${isFlipped ? classes.flipCardFlipped : ""}`}>
                <div className={`${classes.face} ${classes.faceFront}`}>
                  <div
                    className={`${classes.accent} ${accentClass || classes.accentFront}`}
                    aria-hidden="true"
                  />
                  <div className={classes.faceBody}>
                    <span className={classes.sideTag} aria-hidden="true">
                      Front
                    </span>
                    <RowFaceContent
                      cardDetails={cardDetails}
                      detailsOptions={detailsOptions}
                      side="front"
                      classes={classes}
                    />
                  </div>
                </div>
                <div className={`${classes.face} ${classes.faceBack}`}>
                  <div
                    className={`${classes.accent} ${accentClass || classes.accentBack}`}
                    aria-hidden="true"
                  />
                  <div className={classes.faceBody}>
                    <span className={`${classes.sideTag} ${classes.sideTagBack}`} aria-hidden="true">
                      Back
                    </span>
                    <RowFaceContent
                      cardDetails={cardDetails}
                      detailsOptions={detailsOptions}
                      side="back"
                      classes={classes}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showActions ? (
          <div className={classes.actions} onClick={(event) => event.stopPropagation()}>
            <IconButton
              aria-label="Mark wrong"
              size="small"
              className={`${classes.actionBtn} ${classes.wrongBtn} ${answer === false ? "active" : ""}`}
              onClick={() => markAnswer(false)}>
              <ClearIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Mark correct"
              size="small"
              className={`${classes.actionBtn} ${classes.correctBtn} ${answer === true ? "active" : ""}`}
              onClick={() => markAnswer(true)}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </div>
        ) : null}
      </div>
      <CardDetailPopover card={card} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </>
  );
};

const DeckBrowseList = ({
  cards,
  detailsOptions,
  isFlippedCount,
  isRevealedCount,
  showActions,
  onFlippedChange
}: {
  cards: HskEntry[];
  detailsOptions: Details;
  isFlippedCount: number;
  isRevealedCount: number;
  showActions: boolean;
  onFlippedChange?: (order: number, flipped: boolean) => void;
}) => {
  const classes = useStyles();

  return (
    <div className={classes.list}>
      {cards.map((card) => (
        <DeckListRow
          key={card.order}
          card={card}
          detailsOptions={detailsOptions}
          isFlippedCount={isFlippedCount}
          isRevealedCount={isRevealedCount}
          showActions={showActions}
          onFlippedChange={onFlippedChange}
        />
      ))}
    </div>
  );
};

export default DeckBrowseList;
